<template>
  <div class="page">
    <section class="card">
      <h2>Расчет маршрутов из Excel</h2>
      <p class="hint">
        Загрузите Excel с двумя колонками: город отправки и город назначения. В ответ придет enriched файл с ATI city id/name, ATI route id, расстоянием и ценами по типам кузова.
      </p>

      <div class="upload-box" :class="{ active: selectedFile }">
        <input id="routes-file" ref="fileInput" type="file" accept=".xlsx,.xls" @change="handleFileChange" hidden>
        <label for="routes-file" class="file-label">
          {{ selectedFile ? selectedFile.name : 'Выбрать Excel файл' }}
        </label>
        <button type="button" :disabled="!selectedFile || loading" class="send-button" @click="submit">
          <span v-if="loading">Обработка...</span>
          <span v-else>Скачать enriched файл</span>
        </button>
      </div>

      <div class="columns">
        <span>Поддерживаемые названия колонок:</span>
        <code>город отправки</code>
        <code>город назначения</code>
        <code>city_out</code>
        <code>city_in</code>
        <code>from</code>
        <code>to</code>
      </div>
    </section>
  </div>
</template>

<script>
import { ref } from 'vue'
import { createToaster } from '@meforma/vue-toaster'
import { enrichRoutesFile } from '@/backend/routesEnrichmentApi'
import { useErrorHandler } from '@/hooks/errorHandler'

export default {
  name: 'RouteEnrichment',
  setup() {
    const fileInput = ref(null)
    const selectedFile = ref(null)
    const loading = ref(false)
    const toaster = createToaster()

    const handleFileChange = () => {
      selectedFile.value = fileInput.value?.files?.[0] || null
    }

    const contentDispositionFilename = (header) => {
      const match = /filename="?([^";]+)"?/i.exec(header || '')
      return match ? match[1] : 'routes_enriched.xlsx'
    }

    const normalizeBlobError = async (err) => {
      const data = err.response?.data
      if (!(data instanceof Blob)) return err

      const text = await data.text()
      if (!text) return err

      try {
        err.response.data = JSON.parse(text)
      } catch (_) {
        err.response.data = { detail: text }
      }
      return err
    }

    const submit = async () => {
      const file = fileInput.value?.files?.[0]
      if (!file || loading.value) return
      loading.value = true
      try {
        const response = await enrichRoutesFile(file)
        const blob = new Blob([response.data], { type: response.headers['content-type'] })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = contentDispositionFilename(response.headers['content-disposition'])
        link.click()
        window.URL.revokeObjectURL(url)
        toaster.success('Файл рассчитан')
      } catch (err) {
        useErrorHandler(await normalizeBlobError(err))
      } finally {
        loading.value = false
        selectedFile.value = null
        if (fileInput.value) fileInput.value.value = ''
      }
    }

    return {
      fileInput,
      selectedFile,
      loading,
      handleFileChange,
      submit,
    }
  },
}
</script>

<style scoped>
.page {
  min-height: calc(100vh - 40px);
  padding: 32px;
  background: #f3f6fb;
}

.card {
  max-width: 760px;
  padding: 28px;
  border-radius: 18px;
  background: #fff;
  box-shadow: 0 12px 35px rgba(30, 50, 90, 0.12);
}

h2 {
  margin: 0 0 12px;
  color: #1d2b44;
}

.hint {
  margin: 0 0 22px;
  color: #56657a;
  line-height: 1.5;
}

.upload-box {
  display: flex;
  gap: 12px;
  align-items: center;
  padding: 18px;
  border: 1px dashed #a9b7cf;
  border-radius: 14px;
  background: #f8fbff;
}

.upload-box.active {
  border-color: #2d8a57;
  background: #f3fff7;
}

.file-label,
.send-button {
  padding: 11px 16px;
  border-radius: 8px;
  border: none;
  cursor: pointer;
}

.file-label {
  max-width: 360px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #fff;
  background: #3f6fb5;
}

.send-button {
  color: #fff;
  background: #2d8a57;
}

.send-button:disabled {
  cursor: not-allowed;
  background: #9aa6b5;
}

.columns {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  margin-top: 20px;
  color: #56657a;
}

code {
  padding: 4px 7px;
  border-radius: 6px;
  background: #eef3fb;
  color: #284a7c;
}

@media (max-width: 700px) {
  .page {
    padding: 14px;
  }

  .upload-box {
    flex-direction: column;
    align-items: stretch;
  }
}
</style>
